<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <!-- Header -->
    <AdminPageHeader
      title="Lost & Found"
      subtitle="Manage lost and found item reports"
      icon="fas fa-search-location"
      gradient="amber"
    />

    <BRow class="py-2">
      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableLostItems.filter.search"
            placeholder="search items..."
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="3"
      >
        <BFormGroup
          label="Report Type"
          label-for="filterReportType"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableLostItems.selected.reportType"
            placeholder="filter by type"
            input-id="filterReportType"
            label="name"
            clearable
            append-to-body
            :options="reportTypeOptions"
            :disabled="tableLostItems.busy"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ name }">
              <div
                class="text-break text-wrap"
                v-text="name"
              />
            </template>
            <template #no-options="">
              no available type
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Status"
          label-for="filterStatus"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableLostItems.selected.status"
            placeholder="filter by status"
            input-id="filterStatus"
            label="name"
            clearable
            append-to-body
            :options="statusOptions"
            :disabled="tableLostItems.busy"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ name }">
              <div
                class="text-break text-wrap"
                v-text="name"
              />
            </template>
            <template #no-options="">
              no available status
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
      >
        <BFormGroup
          label="Category"
          label-for="filterCategory"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <VSelect
            v-model="tableLostItems.selected.category"
            placeholder="filter category"
            input-id="filterCategory"
            label="name"
            clearable
            append-to-body
            :options="categoryOptions"
            :disabled="tableLostItems.busy"
            :calculate-position="calculateDropPosition"
          >
            <template #option="{ name }">
              <div
                class="text-break text-wrap"
                v-text="name"
              />
            </template>
            <template #no-options="">
              no available category
            </template>
          </VSelect>
        </BFormGroup>
      </BCol>

      <BCol
        cols="12"
        md="2"
        class="d-flex align-items-end"
      >
        <BButton
          size="sm"
          type="button"
          variant="success"
          class="mb-2 btn-osas-primary"
          @click.prevent="refreshTable"
        >
          <i class="fas fa-sync-alt mr-1" /> Refresh
        </BButton>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableLostItems"
      :provider="tableProvider"
      :fields="tableLostItems.fields"
      :filter="tableLostItems.filter"
      :filter-on="tableLostItems.filterOn"
      :sort-by="tableLostItems.sortBy"
      :sort-desc="tableLostItems.sortDesc"
      :sort-direction="tableLostItems.sortDirection"
      :total-rows="tableLostItems.totalRows"
      :current-page="tableLostItems.currentPage"
      :per-page="tableLostItems.perPage"
      :stacked="tableLostItems.stacked"
      :busy="tableLostItems.busy"
      table-class="osas-table"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center gap-2">
          <BButton
            size="sm"
            variant="info"
            class="btn-osas"
            :disabled="tableLostItems.busy"
            @click.prevent="viewItem(item)"
          >
            <i class="fas fa-eye" />
          </BButton>
          <BButton
            size="sm"
            variant="success"
            class="btn-osas"
            :disabled="tableLostItems.busy || item.status === 'claimed' || item.status === 'returned'"
            @click.prevent="markAsClaimedConfirm(item)"
          >
            <i class="fas fa-hand-holding" />
          </BButton>
          <BButton
            size="sm"
            variant="danger"
            class="btn-osas"
            :disabled="tableLostItems.busy"
            @click.prevent="deleteItemConfirm(item)"
          >
            <i class="fas fa-trash" />
          </BButton>
        </div>
      </template>

      <template #cell(report_type)="{ item }">
        <BBadge :variant="item.report_type === 'found' ? 'success' : 'danger'">
          <i
            :class="item.report_type === 'found' ? 'fas fa-box-open' : 'fas fa-question-circle'"
            class="mr-1"
          />
          {{ item.report_type === 'found' ? 'Found' : 'Lost' }}
        </BBadge>
      </template>

      <template #cell(status)="{ item }">
        <BBadge :variant="getStatusVariant(item.status)">
          {{ formatStatus(item.status) }}
        </BBadge>
      </template>

      <template #cell(item_category)="{ item }">
        <BBadge
          variant="primary"
          class="badge-category"
        >
          {{ formatCategory(item.item_category) }}
        </BBadge>
      </template>

      <template #cell(item_image)="{ item }">
        <BButton
          v-if="item.item_image_url"
          size="sm"
          variant="outline-primary"
          :disabled="tableLostItems.busy"
          @click.prevent="viewItem(item)"
        >
          View
        </BButton>
        <span v-else>—</span>
      </template>

      <template #cell(date_lost_found)="{ item }">
        <span class="date-display">
          <i class="fas fa-calendar-alt text-warning mr-1" />
          {{ dateFormatter(item.date_lost_found) }}
        </span>
      </template>
    </CustomTable>

    <!-- View/Edit Modal -->
    <BModal
      ref="modalLostItem"
      size="lg"
      scrollable
      centered
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="modalTitle"
      modal-class="osas-modal"
    >
      <div class="lost-item-details">
        <div class="detail-section">
          <h6 class="section-title">
            <i class="fas fa-user text-success mr-2" />
            Reporter Information
          </h6>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Reference #</span>
              <span class="detail-value">{{ formLostItem.reference_number }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Reporter Name</span>
              <span class="detail-value">{{ formLostItem.reporter_name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Contact</span>
              <span class="detail-value">{{ formLostItem.reporter_contact }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Email</span>
              <span class="detail-value">{{ formLostItem.reporter_email || 'N/A' }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h6 class="section-title">
            <i class="fas fa-box text-warning mr-2" />
            Item Details
          </h6>
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Report Type</span>
              <BBadge :variant="formLostItem.report_type === 'found' ? 'success' : 'danger'">
                {{ formLostItem.report_type === 'found' ? 'Found Item' : 'Lost Item' }}
              </BBadge>
            </div>
            <div class="detail-item">
              <span class="detail-label">Item Name</span>
              <span class="detail-value">{{ formLostItem.item_name }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Category</span>
              <BBadge variant="primary">
                {{ formatCategory(formLostItem.item_category) }}
              </BBadge>
            </div>
            <div class="detail-item">
              <span class="detail-label">Date Lost/Found</span>
              <span class="detail-value">{{ dateFormatter(formLostItem.date_lost_found) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Location</span>
              <span class="detail-value">{{ formLostItem.location_lost_found }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Status</span>
              <BBadge :variant="getStatusVariant(formLostItem.status)">
                {{ formatStatus(formLostItem.status) }}
              </BBadge>
            </div>
          </div>
          <div class="detail-item full-width mt-3">
            <span class="detail-label">Description</span>
            <p class="detail-value description-text">
              {{ formLostItem.item_description }}
            </p>
          </div>

          <div
            v-if="formLostItem.item_image_url"
            class="detail-item full-width mt-3"
          >
            <span class="detail-label">Item Image</span>
            <div class="mt-2">
              <img
                :src="getLfImageUrl(formLostItem.item_image_url)"
                alt="Lost and found item"
                class="img-fluid rounded border"
                style="max-height: 260px;"
              >
            </div>
          </div>
        </div>

        <div
          v-if="formLostItem.claimed_by || formLostItem.claimed_at"
          class="detail-section"
        >
          <h6 class="section-title">
            <i class="fas fa-hand-holding text-info mr-2" />
            Claim Information
          </h6>
          <div class="detail-grid">
            <div
              v-if="formLostItem.claimed_by"
              class="detail-item"
            >
              <span class="detail-label">Claimed By</span>
              <span class="detail-value">{{ formLostItem.claimed_by }}</span>
            </div>
            <div
              v-if="formLostItem.claimed_at"
              class="detail-item"
            >
              <span class="detail-label">Claimed Date</span>
              <span class="detail-value">{{ dateTimeShortFormatter(formLostItem.claimed_at) }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h6 class="section-title">
            <i class="fas fa-cog text-info mr-2" />
            Admin Actions
          </h6>
          <BRow>
            <BCol
              cols="12"
              md="8"
            >
              <BFormGroup
                label="Update Status"
                label-class="font-weight-bolder"
                class="mb-3"
              >
                <VSelect
                  v-model="selectedStatus"
                  placeholder="select new status"
                  label="name"
                  :options="statusOptions"
                  :disabled="state.busy"
                  :calculate-position="calculateDropPosition"
                />
              </BFormGroup>
            </BCol>
          </BRow>
          <small class="text-muted d-block">
            Item details are read-only for submitted reports. Only status changes are allowed here.
          </small>
        </div>
      </div>

      <template #modal-footer="{ cancel }">
        <BButton
          variant="success"
          class="btn-osas-primary"
          :disabled="state.busy"
          @click.prevent="updateLostItemStatus"
        >
          <i class="fas fa-save mr-1" /> Update
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Close
        </BButton>
      </template>
    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import AdminPageHeader from '@/components/core/AdminPageHeader.vue'
import { SAdminLostItem } from '@/services'

export default {
  name: 'VAdminLostAndFound',

  components: {
    AdminPageHeader
  },

  mixins: [
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Lost & Found'
  }),

  data () {
    return {
      state: {
        busy: false
      },
      selectedStatus: null,
      formLostItem: {
        id: '',
        reference_number: '',
        report_type: '',
        reporter_name: '',
        reporter_contact: '',
        reporter_email: '',
        item_name: '',
        item_category: '',
        item_description: '',
        location_lost_found: '',
        item_image_url: '',
        date_lost_found: '',
        status: '',
        claimed_by: '',
        claimed_at: '',
        admin_notes: ''
      },
      statusOptions: [
        { id: 'pending', name: 'Pending' },
        { id: 'verified', name: 'Verified' },
        { id: 'claimed', name: 'Claimed' },
        { id: 'returned', name: 'Returned' },
        { id: 'unclaimed', name: 'Unclaimed' }
      ],
      reportTypeOptions: [
        { id: 'lost', name: 'Lost' },
        { id: 'found', name: 'Found' }
      ],
      categoryOptions: [
        { id: 'electronics', name: 'Electronics' },
        { id: 'documents', name: 'Documents/IDs' },
        { id: 'clothing', name: 'Clothing' },
        { id: 'accessories', name: 'Accessories' },
        { id: 'bags', name: 'Bags/Wallets' },
        { id: 'books', name: 'Books/School Supplies' },
        { id: 'keys', name: 'Keys' },
        { id: 'jewelry', name: 'Jewelry' },
        { id: 'other', name: 'Other' }
      ],
      tableLostItems: {
        busy: false,
        selected: {
          status: null,
          reportType: null,
          category: null
        },
        filter: {
          search: '',
          status: '',
          report_type: '',
          item_category: ''
        },
        filterOn: [],
        stacked: this.isMobile(),
        sortBy: 'created_at',
        sortDesc: true,
        sortDirection: 'desc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 12, key: 'action', class: 'w-md-100px' },
          { mobile: 1, key: 'reference_number', label: 'Ref #', class: 'min-w-120px' },
          { mobile: 2, key: 'report_type', label: 'Type', class: 'min-w-80px' },
          { mobile: 3, key: 'item_name', label: 'Item', class: 'min-w-150px' },
          { mobile: 4, key: 'item_category', label: 'Category', class: 'min-w-100px' },
          { mobile: 5, key: 'item_image', label: 'Image', class: 'min-w-90px' },
          { mobile: 6, key: 'reporter_name', label: 'Reporter', class: 'min-w-130px' },
          { mobile: 7, key: 'location_lost_found', label: 'Location', class: 'min-w-130px' },
          { mobile: 8, key: 'date_lost_found', label: 'Date', class: 'min-w-100px' },
          { mobile: 9, key: 'status', class: 'min-w-90px' },
          { mobile: 10, key: 'created_at', label: 'Reported', class: 'min-w-120px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: Object.assign({
    modalTitle () {
      return 'Item Details'
    }
  }, mapGetters({
    currentUser: 'auth/user'
  })),

  watch: {
    'tableLostItems.selected.status' (status) {
      this.tableLostItems.filter.status = status?.id || ''
    },
    'tableLostItems.selected.reportType' (reportType) {
      this.tableLostItems.filter.report_type = reportType?.id || ''
    },
    'tableLostItems.selected.category' (category) {
      this.tableLostItems.filter.item_category = category?.id || ''
    }
  },

  methods: {
    getPersonFullName (person) {
      if (!person || typeof person !== 'object') return ''
      return [person.first_name, person.middle_name, person.last_name, person.extension_name]
        .filter(Boolean)
        .join(' ')
        .trim()
    },

    getLatestClaim (item) {
      const claims = Array.isArray(item?.claims) ? item.claims : []
      if (!claims.length) return null

      return claims.slice().sort((a, b) => {
        const aTime = new Date(a.claim_date || a.created_at || 0).getTime()
        const bTime = new Date(b.claim_date || b.created_at || 0).getTime()
        return bTime - aTime
      })[0]
    },

    mapApiItem (item) {
      const reportedStudentName = this.getPersonFullName(item.reported_by_student || item.reportedByStudent)
      const reportedUserName = this.getPersonFullName(item.reported_by_user || item.reportedByUser)
      const latestClaim = this.getLatestClaim(item)
      const claimedStudentName = this.getPersonFullName(latestClaim?.claimed_by_student || latestClaim?.claimedByStudent)

      return {
        id: item.id,
        reference_number: item.reference_number || '',
        report_type: item.type,
        reporter_name: item.reporter_name || reportedStudentName || reportedUserName || 'N/A',
        reporter_contact: item.reporter_contact || '',
        reporter_email: item.reporter_email || '',
        item_name: item.title,
        item_category: item.category ? item.category.name : '',
        item_description: item.description,
        location_lost_found: item.location,
        item_image_url: item.image_url || '',
        date_lost_found: item.date_event,
        status: item.status,
        claimed_by: item.claimed_by || claimedStudentName || '',
        claimed_at: item.claimed_at || latestClaim?.claim_date || latestClaim?.created_at || '',
        admin_notes: item.admin_notes || '',
        created_at: item.created_at
      }
    },

    getStatusVariant (status) {
      const variants = {
        pending: 'warning',
        verified: 'info',
        claimed: 'success',
        returned: 'success',
        unclaimed: 'secondary'
      }
      return variants[status] || 'secondary'
    },

    formatStatus (status) {
      return status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A'
    },

    formatCategory (category) {
      return category || 'N/A'
    },

    getStorageFilename (path) {
      if (!path || typeof path !== 'string') return ''
      const parts = path.split('/')
      return parts.length ? parts[parts.length - 1] : ''
    },

    getLfImageUrl (path) {
      const filename = this.getStorageFilename(path)
      if (!filename) return ''

      const apiBase = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '')
      return `${apiBase}/shared/uploads/lf-items/images/${encodeURIComponent(filename)}`
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableLostItems.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        status: filter.status,
        type: filter.report_type,
        lf_item_category: filter.item_category
      }

      return await SAdminLostItem.getList(payload).then(
        ({ data }) => {
          this.tableLostItems.totalRows = data.total_rows
          return (data.items || []).map(item => this.mapApiItem(item))
        }
      ).catch(() => {
        this.tableLostItems.totalRows = 0
        return []
      }).finally(() => {
        this.tableLostItems.busy = false
      })
    },

    refreshTable () {
      this.$refs.tableLostItems.refetch()
    },

    async viewItem (item) {
      this.state.busy = true
      try {
        const { data } = await SAdminLostItem.show(item.id)
        const sourceItem = data.item || item
        this.formLostItem = this.mapApiItem(sourceItem)
      } catch {
        this.formLostItem = { ...item }
      } finally {
        this.selectedStatus = this.statusOptions.find(s => s.id === this.formLostItem.status) || null
        this.state.busy = false
        this.$refs.modalLostItem.show()
      }
    },

    async markAsClaimedConfirm (item) {
      const result = await this.$swal({
        title: 'Mark as Claimed',
        text: `Mark "${item.item_name}" as claimed/returned?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        confirmButtonText: 'Yes, mark it!'
      })

      if (result.isConfirmed) {
        this.state.busy = true
        const status = item.report_type === 'found' ? 'claimed' : 'returned'
        try {
          await SAdminLostItem.updateStatus(item.id, { status })
          this.swalSuccess(`Item marked as ${status} successfully`)
          this.$refs.tableLostItems.refetch()
        } catch (error) {
          this.swalError(error.response?.data?.message || 'Failed to update item status')
        } finally {
          this.state.busy = false
        }
      }
    },

    async updateLostItemStatus () {
      if (!this.selectedStatus) {
        return this.swalInvalid('Please select a status')
      }

      this.state.busy = true
      try {
        await SAdminLostItem.updateStatus(this.formLostItem.id, {
          status: this.selectedStatus.id
        })
        this.swalSuccess('Item status updated successfully').then(() => {
          this.$refs.modalLostItem.hide()
          this.$refs.tableLostItems.refetch()
        })
      } catch {
        this.swalError('Failed to update item status')
      } finally {
        this.state.busy = false
      }
    },

    async deleteItemConfirm (item) {
      const result = await this.$swal({
        title: 'Delete Item',
        text: `Are you sure you want to delete this ${item.report_type} item report for "${item.item_name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      })

      if (result.isConfirmed) {
        this.deleteItem(item.id)
      }
    },

    async deleteItem (id) {
      this.state.busy = true
      try {
        await SAdminLostItem.destroy(id)
        this.swalSuccess('Item report deleted successfully')
        this.$refs.tableLostItems.refetch()
      } catch {
        this.swalError('Failed to delete item')
      } finally {
        this.state.busy = false
      }
    }
  }
}
</script>

<style scoped>
.badge-category {
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
}

.lost-item-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-section {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);
}

.section-title {
  display: flex;
  align-items: center;
  margin: 0 0 0.9rem;
  font-size: 0.97rem;
  font-weight: 700;
  color: #1f2937;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 0.85rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.7rem 0.75rem;
  border: 1px solid #f1f5f9;
  border-radius: 10px;
  background: #f8fafc;
}

.detail-item.full-width {
  width: 100%;
}

.detail-label {
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.detail-value {
  font-size: 0.92rem;
  font-weight: 500;
  color: #0f172a;
  word-break: break-word;
}

.description-text {
  background: #fff;
  padding: 0.85rem;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  margin-top: 0.25rem;
  line-height: 1.5;
}

.lost-item-details img {
  width: 100%;
  max-width: 460px;
  object-fit: cover;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.1);
}

@media (max-width: 767px) {
  .detail-section {
    padding: 0.85rem;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
